import { useEffect } from "react"
import { useLazyQuery } from "@apollo/client"
import * as gqlTypes from "@hydrologiq/gql/graphql"

export interface UseCustomerPerformanceOptions {
  customerId?: string
  startDate: string
  endDate: string
  includeScheduled?: boolean
  enabled?: boolean
}

export interface UseCustomerPerformanceResult {
  dailySummary: any[]
  summary?: any
  loading: boolean
  error?: Error
  refetch: () => void
}

export function useCustomerPerformance(options: UseCustomerPerformanceOptions): UseCustomerPerformanceResult {
  const { customerId, startDate, endDate, includeScheduled = true, enabled = true } = options

  const shouldSkip = !enabled || !customerId || !startDate || !endDate || startDate > endDate

  const [fetchPerformance, { data, loading, error }] = useLazyQuery(gqlTypes.GetCustomerPerformanceDocument, {
    fetchPolicy: "cache-first",
  })

  // Trigger fetch when inputs change
  useEffect(() => {
    if (!shouldSkip) {
      fetchPerformance({
        variables: {
          customer_id: customerId || "",
          start_date: startDate,
          end_date: endDate,
          include_scheduled: includeScheduled,
        },
      })
    }
  }, [customerId, startDate, endDate, includeScheduled, shouldSkip, fetchPerformance])

  const performance = data?.customerPerformance
  const dailySummary = performance?.daily_summary || []
  const summary = performance?.summary

  // Handle refetch
  const handleRefetch = () => {
    if (customerId) {
      fetchPerformance({
        variables: {
          customer_id: customerId,
          start_date: startDate,
          end_date: endDate,
          include_scheduled: includeScheduled,
        },
      })
    }
  }

  return {
    dailySummary,
    summary,
    loading,
    error: error as Error | undefined,
    refetch: handleRefetch,
  }
}

